import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import {
  MDSButton,
  MDSButtonIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

interface Props {
  currentSessionId: string;
  isClosed?: boolean;
}

export const EmptyOverlay = observer(({ currentSessionId, isClosed = false }: Props) => {
  const { pageStore } = useAppStore();
  const memItStore = pageStore.memItPage;

  const overlayRef = useRef<HTMLDivElement>(null);
  const resumeRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const lastResumeSizeRef = useRef<{ width: number; height: number } | null>(null);
  const [hideResumeDueToCollision, setHideResumeDueToCollision] = useState(false);

  const showEmptyState = memItStore.sessionEvents.length === 0 && !isClosed;
  const [showResumeButton, setShowResumeButton] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSessionId, setRecentSessionId] = useState<string | undefined>();

  useEffect(() => {
    if (!showEmptyState) {
      setShowResumeButton(false);
      setShowSuggestions(false);
      setRecentSessionId(undefined);
      return;
    }

    const recent = memItStore.previousChatSessions[0];
    if (recent && recent.modelId !== currentSessionId) {
      setShowResumeButton(memItStore.sessionEvents.length === 0);
      setShowSuggestions(memItStore.sessionEvents.length === 0);
      setRecentSessionId(recent.modelId);
      return;
    }

    if (!recent) {
      setShowResumeButton(false);
      setShowSuggestions(true);
      setRecentSessionId(undefined);
      return;
    }

    setShowResumeButton(false);
    setShowSuggestions(false);
    setRecentSessionId(undefined);
  }, [currentSessionId, showEmptyState, memItStore, memItStore.previousChatSessions]);

  const handleResumePastSession = () => {
    if (recentSessionId) {
      memItStore.handleSelectSession(recentSessionId);
    }
  };

  const handleSendSuggestion = useCallback(
    async (message: string) => {
      trackEvent(TrackedEvent.ChatMessageSendFromSuggestion, { message });
      await memItStore.handleCreateUserMessage({ content: message, context: {} });
    },
    [memItStore]
  );

  // Detect overlap between floating resume and suggestions containers and hide resume if needed
  const checkCollision = useCallback(() => {
    if (!overlayRef.current) {
      return;
    }

    const containerRect = overlayRef.current.getBoundingClientRect();

    // Determine resume rect from live node if present; otherwise synthesize using last known size
    let resumeRect: DOMRect | { left: number; right: number; top: number; bottom: number } | null =
      null;

    if (resumeRef.current) {
      const liveRect = resumeRef.current.getBoundingClientRect();
      // cache last known size for when the node is unmounted
      lastResumeSizeRef.current = { width: liveRect.width, height: liveRect.height };
      resumeRect = liveRect;
    } else if (lastResumeSizeRef.current) {
      const { width, height } = lastResumeSizeRef.current;
      // FloatingResumeContainer styles: top: 80px; left: 50%; transform: translateX(-50%)
      const top = containerRect.top + 80;
      const centerX = containerRect.left + containerRect.width / 2;
      const left = centerX - width / 2;
      resumeRect = { left, right: left + width, top, bottom: top + height };
    }

    // If we still can't determine resume rect, don't toggle state to avoid flicker
    if (!resumeRect || !suggestionsRef.current) {
      return;
    }

    const suggestionsRect = suggestionsRef.current.getBoundingClientRect();

    // Explicit overlap-only check (touching edges does NOT count as overlap)
    const rectanglesOverlap = !(
      resumeRect.right <= suggestionsRect.left ||
      resumeRect.left >= suggestionsRect.right ||
      resumeRect.bottom <= suggestionsRect.top ||
      resumeRect.top >= suggestionsRect.bottom
    );

    setHideResumeDueToCollision(rectanglesOverlap);
  }, []);

  useLayoutEffect(() => {
    if (!showSuggestions) {
      setHideResumeDueToCollision(false);
      return;
    }

    // Run after layout for accurate measurements
    const raf = requestAnimationFrame(checkCollision);

    const handleWindowResize = () => requestAnimationFrame(checkCollision);
    window.addEventListener("resize", handleWindowResize);

    const resizeObserver: ResizeObserver | undefined = new ResizeObserver(() => {
      requestAnimationFrame(checkCollision);
    });
    if (overlayRef.current) {
      resizeObserver.observe(overlayRef.current);
    }
    if (suggestionsRef.current) {
      resizeObserver.observe(suggestionsRef.current);
    }
    if (resumeRef.current) {
      resizeObserver.observe(resumeRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleWindowResize);
      resizeObserver?.disconnect();
    };
  }, [showSuggestions, checkCollision]);

  const showOverlay = showResumeButton || showSuggestions;

  return (
    <OverlayRoot ref={overlayRef} data-component="EmptyOverlay">
      {showOverlay && (
        <>
          <AnimatePresence initial={false}>
            {!hideResumeDueToCollision && showResumeButton && (
              <motion.div
                key="resume-fab"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: "auto" }}
              >
                <FloatingResumeContainer ref={resumeRef} data-component="FloatingResumeContainer">
                  <MDSButton
                    variant={MDSButtonVariant.Transparent}
                    onClick={handleResumePastSession}
                  >
                    <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.ArrowUp} />
                    Resume last Chat
                  </MDSButton>
                </FloatingResumeContainer>
              </motion.div>
            )}
          </AnimatePresence>
          {showSuggestions && (
            <FloatingSuggestionsContainer
              ref={suggestionsRef}
              data-component="FloatingSuggestionsContainer"
            >
              <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
                Ask about your notes
              </MDSText>
              <MDSButton
                variant={MDSButtonVariant.ContrastAccent}
                onClick={() => handleSendSuggestion("What should I follow up on from this week?")}
              >
                What should I follow up on from this week?
              </MDSButton>
              <MDSButton
                variant={MDSButtonVariant.ContrastAccent}
                onClick={() => handleSendSuggestion("Give me an overview of my last 14 days")}
              >
                Give me an overview of my last 14 days
              </MDSButton>
            </FloatingSuggestionsContainer>
          )}
        </>
      )}
    </OverlayRoot>
  );
});

const OverlayRoot = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const FloatingResumeContainer = styled.div({
  position: "absolute",
  top: "50px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
  pointerEvents: "auto",
});

const FloatingSuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  align-items: center;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: auto;
`;

export default EmptyOverlay;
